<template>
  <div
    ref="page"
    class="cold-data-page"
    :class="{ 'is-landscape': isLandscape, 'force-landscape': forceLandscape }"
  >
    <!-- <header class="page-nav">
      <button type="button" class="back-button" aria-label="返回" @click="goBack">
        <van-icon name="arrow-left" />
      </button>
      <span class="page-title">订单冷藏数据</span>
      <span class="nav-placeholder"></span>
    </header> -->

    <main class="chart-card">
      <div class="chart-header">
        <h1>设备温湿度记录</h1>
        <button type="button" class="landscape-button" @click="toggleLandscape">
          <van-icon :name="isLandscape ? 'shrink-o' : 'expand-o'" />
          <span>{{ isLandscape ? '退出横屏' : '横屏' }}</span>
        </button>
      </div>
      <div v-if="loading" class="chart-placeholder">数据加载中...</div>
      <div v-else-if="!hasData" class="chart-placeholder">暂无温湿度数据</div>
      <div v-show="hasData" ref="chart" class="sensor-chart"></div>
    </main>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import { Icon, Toast } from 'vant';
import { getColdDataDetail } from '@/api/shareOrderDetails';

export default {
  name: 'ShareOrderColdData',
  components: {
    [Icon.name]: Icon
  },
  data() {
    return {
      chart: null,
      loading: true,
      temperatureData: [],
      humidityData: [],
      isLandscape: false,
      forceLandscape: false,
      fullscreenActive: false,
      orientationLockPending: false
    };
  },
  computed: {
    hasData() {
      return this.temperatureData.length > 0 || this.humidityData.length > 0;
    },
    timeAxis() {
      const times = this.temperatureData.concat(this.humidityData).map(item => item.timestamp);
      return Array.from(new Set(times)).sort((a, b) => a - b);
    }
  },
  mounted() {
    this.loadSensorData();
    window.addEventListener('resize', this.handleViewportChange);
    window.addEventListener('orientationchange', this.syncLandscapeFallback);
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleViewportChange);
    window.removeEventListener('orientationchange', this.syncLandscapeFallback);
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    this.unlockOrientation();
    if (this.chart) this.chart.dispose();
  },
  methods: {
    async loadSensorData() {
      const shareCode = this.$route.query.shareCode;
      if (!shareCode) {
        this.loading = false;
        Toast.fail('缺少分享码');
        return;
      }
      try {
        const { data } = await getColdDataDetail({ shareCode });
        this.temperatureData = this.normalizeSeries(data && data.temperatureList);
        this.humidityData = this.normalizeSeries(data && data.humidityList);
        await this.$nextTick();
        this.renderChart();
      } catch (error) {
        console.error('获取冷藏数据失败', error);
        Toast.fail('获取冷藏数据失败');
      } finally {
        this.loading = false;
      }
    },
    normalizeSeries(list) {
      if (!Array.isArray(list)) return [];
      const readings = [];
      list.forEach(item => {
        let values = item && item.sensorValue;
        if (typeof values === 'string') {
          try {
            values = JSON.parse(values);
          } catch (error) {
            values = [{ sensorValue: values, time: item.time }];
          }
        }
        const source = Array.isArray(values) ? values : [values || item];
        source.forEach(reading => {
          const timestamp = this.toSecondTimestamp(
            reading && (reading.time || reading.lastUpdateTime || item.time)
          );
          const rawValue = Number(reading && reading.sensorValue);
          if (!timestamp || Number.isNaN(rawValue)) return;
          readings.push({
            timestamp,
            value: Number(this.$math(rawValue, 10).divide())
          });
        });
      });
      const unique = {};
      readings.forEach(item => {
        unique[item.timestamp] = item;
      });
      return Object.keys(unique).map(key => unique[key]).sort((a, b) => a.timestamp - b.timestamp);
    },
    toTimestamp(value) {
      if (value == null || value === '') return 0;
      const timestamp = Number(value);
      if (!Number.isNaN(timestamp) && timestamp > 0) return timestamp < 100000000000 ? timestamp * 1000 : timestamp;
      const parsed = new Date(String(value).replace(/-/g, '/')).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    },
    toSecondTimestamp(value) {
      const timestamp = this.toTimestamp(value);
      return timestamp ? Math.floor(timestamp / 1000) * 1000 : 0;
    },
    renderChart() {
      if (!this.hasData || !this.$refs.chart) return;
      if (!this.chart) this.chart = echarts.init(this.$refs.chart);
      const axis = this.timeAxis;
      const temperatureMap = this.toSeriesMap(this.temperatureData);
      const humidityMap = this.toSeriesMap(this.humidityData);
      this.chart.setOption({
        animation: false,
        color: ['#0074e4', '#86dc91'],
        grid: {
          top: 72,
          left: 38,
          right: 38,
          bottom: 72
        },
        legend: {
          top: 8,
          right: 8,
          itemWidth: 12,
          itemHeight: 12,
          itemGap: 22,
          textStyle: { color: '#3d3833', fontSize: 14 },
          data: ['温度', '湿度']
        },
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(61, 56, 51, .78)',
          borderWidth: 0,
          padding: [10, 12],
          textStyle: { color: '#fff', fontSize: 13 },
          formatter: params => this.formatTooltip(params)
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: axis,
          axisLine: { lineStyle: { color: '#d8d5d2' } },
          axisTick: { show: false },
          axisLabel: {
            color: '#3d3833',
            fontSize: 12,
            formatter: value => this.formatTime(value),
            interval: 0
          }
        },
        dataZoom: [
          {
            type: 'slider',
            xAxisIndex: 0,
            start: 0,
            end: 20,
            zoomLock: false,
            disabled: false,
            brushSelect: true,
            showDetail: false,
            height: 20,
            bottom: 16
          }
        ],
        yAxis: [
          {
            type: 'value',
            name: '温度℃',
            nameTextStyle: { color: '#85807b', fontSize: 12, align: 'left' },
            axisLabel: { color: '#3d3833', fontSize: 12 },
            splitLine: { lineStyle: { type: 'dashed', color: '#eeeeee' } }
          },
          {
            type: 'value',
            name: '湿度（%）',
            nameTextStyle: { color: '#85807b', fontSize: 12, align: 'right' },
            axisLabel: { color: '#3d3833', fontSize: 12, formatter: '{value}%' },
            splitLine: { show: false },
            min: 0,
            max: value => Math.max(100, Math.ceil(value.max / 10) * 10)
          }
        ],
        series: [
          {
            name: '温度',
            type: 'line',
            yAxisIndex: 0,
            connectNulls: false,
            showSymbol: true,
            showAllSymbol: true,
            smooth: true,
            data: axis.map(time => temperatureMap[time] == null ? null : temperatureMap[time]),
            lineStyle: { width: 1 },
            itemStyle: { color: '#0074e4' }
          },
          {
            name: '湿度',
            type: 'line',
            yAxisIndex: 1,
            connectNulls: false,
            showSymbol: true,
            showAllSymbol: true,
            smooth: true,
            data: axis.map(time => humidityMap[time] == null ? null : humidityMap[time]),
            lineStyle: { width: 1 },
            itemStyle: { color: '#86dc91' }
          }
        ]
      }, true);
    },
    toSeriesMap(series) {
      return series.reduce((result, item) => {
        result[item.timestamp] = item.value;
        return result;
      }, {});
    },
    formatTime(value) {
      const date = new Date(Number(value));
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    },
    formatDateTime(value) {
      const date = new Date(Number(value));
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${date.getFullYear()}-${month}-${day} ${this.formatTime(value)}`;
    },
    formatTooltip(params) {
      const rows = params.filter(item => item.value !== null && item.value !== undefined);
      if (!rows.length) return '';
      const content = rows.map(item => {
        const unit = item.seriesName === '温度' ? '℃' : '%';
        return `${item.marker}${item.seriesName}：${item.value}${unit}`;
      }).join('<br/>');
      return `${this.formatDateTime(params[0].axisValue)}<br/>${content}`;
    },
    mobileLabelInterval(length) {
      return Math.max(0, Math.ceil(length / 4) - 1);
    },
    resizeChart() {
      if (this.chart) this.chart.resize();
    },
    handleViewportChange() {
      if (this.isLandscape) this.syncLandscapeFallback();
      else this.resizeChart();
    },
    getFullscreenElement() {
      return document.fullscreenElement
        || document.webkitFullscreenElement
        || document.webkitCurrentFullScreenElement;
    },
    isFullscreenActive() {
      return Boolean(this.getFullscreenElement() || document.webkitIsFullScreen);
    },
    isVivoBrowser() {
      return /vivo|vivobrowser/i.test(window.navigator.userAgent);
    },
    requestPageFullscreen() {
      const page = this.$refs.page;
      if (!page) return Promise.resolve();
      const requestFullscreen = page.requestFullscreen || page.webkitRequestFullscreen;
      if (!requestFullscreen) return Promise.resolve();
      const result = requestFullscreen.call(page);
      return result && typeof result.then === 'function' ? result : Promise.resolve();
    },
    async toggleLandscape() {
      if (this.isLandscape) {
        await this.exitLandscape();
        return;
      }
      this.isLandscape = true;
      if (this.isVivoBrowser()) {
        // vivo 会自动退出原生全屏并恢复竖屏，因此直接保留 CSS 旋转后的横屏布局。
        this.syncLandscapeFallback();
        await this.$nextTick();
        this.resizeChart();
        return;
      }
      let shouldFallback = false;
      // 原生方向锁执行期间不提前旋转 CSS，避免先闪一次全屏再进入横屏。
      this.orientationLockPending = true;
      try {
        await this.requestPageFullscreen();
        this.fullscreenActive = this.fullscreenActive || this.isFullscreenActive();
        if (window.screen.orientation && window.screen.orientation.lock) {
          await window.screen.orientation.lock('landscape');
        } else {
          throw new Error('Screen Orientation API is not supported');
        }
        this.forceLandscape = false;
      } catch (error) {
        shouldFallback = true;
        console.warn('当前浏览器不支持锁定横屏，将使用横屏布局展示', error);
      } finally {
        this.orientationLockPending = false;
      }
      if (shouldFallback) this.syncLandscapeFallback();
      this.$nextTick(this.resizeChart);
    },
    async exitLandscape() {
      const exitFullscreen = document.exitFullscreen
        || document.webkitExitFullscreen
        || document.webkitCancelFullScreen;
      if (this.isFullscreenActive() && exitFullscreen) {
        const result = exitFullscreen.call(document);
        if (result && typeof result.then === 'function') await result;
      }
      this.fullscreenActive = false;
      this.isLandscape = false;
      this.forceLandscape = false;
      this.unlockOrientation();
      await this.$nextTick();
      this.resizeChart();
    },
    syncLandscapeFallback() {
      if (!this.isLandscape) return;
      if (this.orientationLockPending) {
        this.$nextTick(this.resizeChart);
        return;
      }
      // 部分 Android 浏览器的 orientation 媒体查询不可靠，视口宽高更接近实际显示方向。
      this.forceLandscape = window.innerHeight >= window.innerWidth;
      this.$nextTick(this.resizeChart);
    },
    unlockOrientation() {
      if (window.screen.orientation && window.screen.orientation.unlock) {
        window.screen.orientation.unlock();
      }
    },
    async handleFullscreenChange() {
      if (this.isFullscreenActive()) {
        this.fullscreenActive = true;
      } else if (this.fullscreenActive) {
        // vivo 的全屏属性更新晚于事件，短暂等待后再判断是否真的退出。
        await new Promise(resolve => setTimeout(resolve, 100));
        if (this.isFullscreenActive()) {
          this.fullscreenActive = true;
          await this.$nextTick();
          this.resizeChart();
          return;
        }
        this.fullscreenActive = false;
        this.isLandscape = false;
        this.forceLandscape = false;
        this.unlockOrientation();
      }
      await this.$nextTick();
      this.resizeChart();
    },
    goBack() {
      const exitFullscreen = document.exitFullscreen
        || document.webkitExitFullscreen
        || document.webkitCancelFullScreen;
      if (this.isFullscreenActive() && exitFullscreen) {
        exitFullscreen.call(document);
        return;
      }
      if (window.history.length > 1) this.$router.back();
    }
  }
};
</script>

<style scoped lang="scss">
.cold-data-page {
  min-height: 100vh;
  background: #f8f8f8;
  color: #3d3833;
  font-family: "Source Han Sans CN", Arial, sans-serif;
}

.page-nav {
  height: 44px;
  padding: 0 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
}

.page-title {
  font-size: 16px;
  color: #000;
}

.back-button,
.nav-placeholder {
  width: 48px;
}

.back-button {
  border: 0;
  background: transparent;
  padding: 8px;
  text-align: left;
  font-size: 20px;
  color: #000;
}

.chart-card {
  min-height: 380px;
  margin-top: 8px;
  padding: 18px 0 0;
  background: #fff;
}

.chart-header {
  height: 28px;
  padding: 0 12px 0 19px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.chart-header h1 {
  position: relative;
  margin: 0;
  padding-left: 0;
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
}

.chart-header h1::before {
  position: absolute;
  top: 4px;
  left: -7px;
  width: 3px;
  height: 16px;
  background: #f1892e;
  content: "";
}

.landscape-button {
  display: flex;
  align-items: center;
  gap: 4px;
  border: 0;
  padding: 4px;
  background: transparent;
  color: #f1892e;
  font-size: 13px;
}

.sensor-chart {
  width: 100%;
  height: 300px;
}

.chart-placeholder {
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #85807b;
  font-size: 14px;
}

.cold-data-page:fullscreen,
.cold-data-page.is-landscape {
  min-height: 100vh;
  background: #fff;
}

.cold-data-page:fullscreen .page-nav,
.cold-data-page.is-landscape .page-nav {
  display: none;
}

.cold-data-page:fullscreen .chart-card,
.cold-data-page.is-landscape .chart-card {
  box-sizing: border-box;
  min-height: 100vh;
  margin: 0;
  padding: 18px calc(50px + env(safe-area-inset-bottom, 0px)) 0 0;
}

.cold-data-page:fullscreen .sensor-chart,
.cold-data-page.is-landscape .sensor-chart {
  height: calc(100vh - env(safe-area-inset-bottom, 100px));
  padding-right: env(safe-area-inset-bottom, 100px);
  min-height: 300px;
}

.cold-data-page:fullscreen .chart-placeholder,
.cold-data-page.is-landscape .chart-placeholder {
  height: calc(100vh - env(safe-area-inset-bottom, 0px));
}

.cold-data-page.force-landscape {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vh;
  height: 100vw;
  min-height: 0;
  overflow: auto;
  transform: rotate(90deg) translateY(-100%);
  transform-origin: top left;
}

.cold-data-page.force-landscape .chart-card {
  min-height: 100vw;
}

.cold-data-page.force-landscape .sensor-chart,
.cold-data-page.force-landscape .chart-placeholder {
  height: calc(100vw - 50px - env(safe-area-inset-bottom, 0px));
  padding-right:20px;
}
</style>
